import { modelInfo, repoExists } from '@huggingface/hub'
import type { ToastStore } from '@skeletonlabs/skeleton'

import { encodeHTMLEntities } from '@utils/encode'

const HF_CONVERT_BRANCH = 'refs/convert/parquet'

type ConvertBranchStatus =
  | 'insufficient-data'
  | 'already-using'
  | 'non-default-branch'
  | 'available'
  | 'missing'
  | 'error'

interface ConvertBranchCheckOptions {
  datasetId?: string | null
  currentBranch?: string | null
  token?: string | null
  fetchFn?: typeof fetch
}

const getConvertBranchStatus = async ({
  datasetId,
  currentBranch,
  token,
  fetchFn,
}: ConvertBranchCheckOptions): Promise<ConvertBranchStatus> => {
  const normalizedDatasetId = datasetId?.trim()
  const normalizedBranch = currentBranch?.trim()

  if (!normalizedDatasetId || !normalizedBranch) {
    return 'insufficient-data'
  }

  if (normalizedBranch === HF_CONVERT_BRANCH) {
    return 'already-using'
  }

  if (normalizedBranch.toLowerCase() !== 'main') {
    return 'non-default-branch'
  }

  try {
    const convertBranchExists = await repoExists({
      repo: {
        name: normalizedDatasetId,
        type: 'dataset',
      },
      revision: HF_CONVERT_BRANCH,
      accessToken: token ?? undefined,
      fetch: fetchFn ?? fetch,
    })

    return convertBranchExists ? 'available' : 'missing'
  } catch (error) {
    console.warn('Failed to determine HuggingFace parquet branch availability', error)
    return 'error'
  }
}

interface RecommendConvertBranchOptions extends ConvertBranchCheckOptions {
  toastStore: ToastStore
  confirm?: (message: string) => boolean | Promise<boolean>
  onApplyBranch?: (branch: string) => void
}

const recommendConvertBranch = async ({
  datasetId,
  currentBranch,
  token,
  fetchFn,
  toastStore,
  confirm,
  onApplyBranch,
}: RecommendConvertBranchOptions): Promise<boolean> => {
  const status = await getConvertBranchStatus({
    datasetId,
    currentBranch,
    token,
    fetchFn,
  })

  if (status !== 'available' || !datasetId || !currentBranch) {
    return false
  }

  const normalizedDatasetId = datasetId.trim()
  const normalizedBranch = currentBranch.trim()
  if (!normalizedDatasetId || !normalizedBranch) {
    return false
  }

  const encodedDatasetId = encodeHTMLEntities(normalizedDatasetId)
  const encodedCurrentBranch = encodeHTMLEntities(normalizedBranch)
  const encodedConvertBranch = encodeHTMLEntities(HF_CONVERT_BRANCH)
  const promptMessage = `Dataset ${normalizedDatasetId} exposes a ${HF_CONVERT_BRANCH} revision optimized for parquet exports. Use it instead of ${normalizedBranch}?`

  if (confirm) {
    const shouldSwitch = await confirm(promptMessage)
    if (shouldSwitch) {
      onApplyBranch?.(HF_CONVERT_BRANCH)
      toastStore.trigger({
        message: `Updated HuggingFace branch to <strong>${encodedConvertBranch}</strong>.`,
        background: 'variant-filled-success',
      })
      return true
    }

    toastStore.trigger({
      message: `Continuing with <strong>${encodedCurrentBranch}</strong>. You can switch to <strong>${encodedConvertBranch}</strong> for faster dataset usage.`,
      background: 'variant-filled-warning',
      autohide: false,
    })
    return false
  }

  toastStore.trigger({
    message: `Dataset <strong>${encodedDatasetId}</strong> has a <strong>${encodedConvertBranch}</strong> revision. Update the HuggingFace storage config to leverage parquet exports.`,
    background: 'variant-filled-warning',
    autohide: false,
  })
  return false
}

export { getConvertBranchStatus, recommendConvertBranch }

interface HuggingFaceModelAccessOptions {
  modelId?: string | null
  token?: string | null
  fetchFn?: typeof fetch
}

type HuggingFaceModelAccessStatus = 'insufficient-data' | 'accessible' | 'unauthorized' | 'error'

interface HuggingFaceModelAccessResult {
  status: HuggingFaceModelAccessStatus
  parameterCount: number | null
  isPrivate: boolean
  isGated: boolean
}

const sumSafetensorsParameters = (parameters?: Record<string, number>): number | null => {
  if (!parameters) {
    return null
  }
  const total = Object.values(parameters).reduce((sum, value) => sum + value, 0)
  return Number.isFinite(total) && total > 0 ? total : null
}

const checkHuggingFaceModelAccess = async ({
  modelId,
  token,
  fetchFn,
}: HuggingFaceModelAccessOptions): Promise<HuggingFaceModelAccessResult> => {
  const normalizedModelId = modelId?.trim()
  if (!normalizedModelId) {
    return {
      status: 'insufficient-data',
      parameterCount: null,
      isPrivate: false,
      isGated: false,
    }
  }

  try {
    const modelDetails = await modelInfo<'safetensors'>({
      name: normalizedModelId,
      accessToken: token ?? undefined,
      fetch: fetchFn ?? fetch,
      additionalFields: ['safetensors'],
    })
    const parameterCount =
      modelDetails.safetensors?.total ??
      sumSafetensorsParameters(modelDetails.safetensors?.parameters)
    const isPrivate = Boolean(modelDetails.private)
    const isGated = Boolean(modelDetails.gated)

    return {
      status: 'accessible',
      parameterCount,
      isPrivate,
      isGated,
    }
  } catch (error) {
    const statusCode = (error as { status?: number })?.status
    const status = statusCode === 401 || statusCode === 403 ? 'unauthorized' : 'error'
    return {
      status,
      parameterCount: null,
      isPrivate: false,
      isGated: false,
    }
  }
}

export { checkHuggingFaceModelAccess }
